import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api, mediaUrl } from "../lib/api";
import { CheckCircle2, Circle, ChevronLeft } from "lucide-react";
import { LectureQA } from "../components/LectureQA";
import { ReviewDialog } from "../components/ReviewDialog";
import { toast } from "sonner";

export default function PlayerPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [current, setCurrent] = useState(null);
  const [anns, setAnns] = useState([]);
  const [unseen, setUnseen] = useState([]);
  const [myReview, setMyReview] = useState(null);
  const [showReview, setShowReview] = useState(false);
  const [qualities, setQualities] = useState([]);
  const [selected, setSelected] = useState("auto");
  const [speed, setSpeed] = useState(1);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsView, setSettingsView] = useState("root");
  const videoRef = useRef(null);
  const seeked = useRef(false);
  const pendingSeek = useRef(null);
  const userChose = useRef(false);
  const stalls = useRef([]);

  useEffect(() => {
    setQualities([]);
    setSelected("auto");
    setSettingsOpen(false);
    pendingSeek.current = null;
    userChose.current = false;
    stalls.current = [];
    const vurl = current?.video_url || "";
    if (vurl.includes("/api/media/")) {
      const fid = vurl.split("/api/media/")[1].split("?")[0];
      api.get(`/media/${fid}/info`).then((r) => setQualities(r.data.qualities || [])).catch(() => {});
    }
  }, [current]);

  const has480 = qualities.some((o) => o.q === "480");
  const srcLabel = qualities.find((o) => o.q === null)?.label || "Original";
  const effQ = selected === "auto" ? (has480 ? "480" : null) : selected;
  const qualityLabel = selected === "auto"
    ? `Auto (${has480 ? "480p" : srcLabel})`
    : (qualities.find((o) => o.q === selected)?.label || srcLabel);

  const selectQuality = (v) => {
    if (v === selected) return;
    userChose.current = v !== "auto";
    pendingSeek.current = videoRef.current?.currentTime || 0;
    setSelected(v);
  };

  const selectSpeed = (s) => {
    setSpeed(s);
    if (videoRef.current) videoRef.current.playbackRate = s;
  };

  const onWaiting = () => {
    const t = Date.now();
    stalls.current = [...stalls.current.filter((x) => t - x < 30000), t];
    if (userChose.current || stalls.current.length < 3) return;
    const lower = qualities.filter((o) => o.q).map((o) => o.q).sort((a, b) => b - a);
    const idx = effQ == null ? -1 : lower.indexOf(effQ);
    const next = lower[idx + 1];
    if (next) {
      stalls.current = [];
      pendingSeek.current = videoRef.current?.currentTime || 0;
      userChose.current = true;
      setSelected(next);
      toast.info(`Slow connection detected — switched to ${next}p`);
    }
  };

  useEffect(() => {
    api.get(`/courses/${id}/player`).then((r) => {
      setData(r.data);
      const lects = r.data.course.curriculum.flatMap((s) => s.lectures);
      const resume = lects.find((l) => l.id === r.data.last_lecture_id) || lects[0];
      setCurrent(resume);
    }).catch(() => setData(false));
    api.get(`/courses/${id}/announcements`).then((r) => setAnns(r.data)).catch(() => {});
    api.get(`/qa/unseen-answers?course_id=${id}`).then((r) => {
      setUnseen(r.data);
      if (r.data.length > 0) toast.info(`📬 The instructor answered ${r.data.length} of your question${r.data.length > 1 ? "s" : ""}!`);
    }).catch(() => {});
    api.get(`/reviews/mine?course_id=${id}`).then((r) => setMyReview(r.data.id ? r.data : null)).catch(() => {});
  }, [id]);

  const progMap = useMemo(() => {
    const m = {};
    (data?.progress || []).forEach((p) => { m[p.lecture_id] = p; });
    return m;
  }, [data]);

  if (data === null) return <p className="p-10 text-[var(--forge-dim)]">Loading…</p>;
  if (data === false) return (
    <div className="p-10 text-center text-[var(--forge-dim)]">
      <p>You don't have access to this course.</p>
      <Link to={`/course/${id}`} className="btn-primary inline-block mt-4">VIEW COURSE</Link>
    </div>
  );

  const { course } = data;
  const allLects = course.curriculum.flatMap((s) => s.lectures);
  const doneCount = allLects.filter((l) => progMap[l.id]?.completed).length;
  const pct = allLects.length ? Math.round((doneCount / allLects.length) * 100) : 0;

  const save = (lecture, completed, position) => {
    api.post("/progress", { course_id: id, lecture_id: lecture.id, completed, position }).then(() => {
      setData((d) => {
        const others = d.progress.filter((p) => p.lecture_id !== lecture.id);
        return { ...d, progress: [...others, { lecture_id: lecture.id, completed, position }] };
      });
    }).catch(() => {});
  };

  const selectLecture = (l) => {
    seeked.current = false;
    setCurrent(l);
    save(l, progMap[l.id]?.completed || false, progMap[l.id]?.position || 0);
  };

  const onEnded = () => {
    save(current, true, 0);
    const idx = allLects.findIndex((l) => l.id === current.id);
    const remaining = allLects.filter((l) => l.id !== current.id && !progMap[l.id]?.completed).length;
    if (remaining === 0) {
      toast.success("🏆 Course complete! Your certificate is ready.");
      if (!myReview) setTimeout(() => setShowReview(true), 1200);
    }
    else toast.success("Lecture completed!");
    if (idx < allLects.length - 1) selectLecture(allLects[idx + 1]);
  };

  const onLoaded = () => {
    if (videoRef.current) videoRef.current.playbackRate = speed;
    if (pendingSeek.current != null && videoRef.current) {
      videoRef.current.currentTime = pendingSeek.current;
      pendingSeek.current = null;
      videoRef.current.play().catch(() => {});
      return;
    }
    const pos = progMap[current.id]?.position;
    if (pos && !seeked.current && videoRef.current) {
      videoRef.current.currentTime = pos;
      seeked.current = true;
    }
  };

  const onPause = () => {
    if (videoRef.current && !videoRef.current.ended)
      save(current, progMap[current.id]?.completed || false, videoRef.current.currentTime);
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 pt-20 pb-6">
      <div className="flex items-center gap-3 mb-4">
        <Link to="/my-learning" className="text-[var(--forge-dim)] hover:text-[var(--forge-blue)]"><ChevronLeft size={20} /></Link>
        <h1 className="font-display font-bold text-lg flex-1 truncate" data-testid="player-course-title">{course.title}</h1>
        {pct === 100 && (
          <button data-testid="rate-course-btn" onClick={() => setShowReview(true)}
            className="text-xs font-display px-3 py-1.5 rounded border transition-colors hover:bg-[rgba(56,182,255,.12)]"
            style={{ color: "var(--bp)", borderColor: "rgba(56,182,255,.5)", letterSpacing: 1 }}>
            ★ {myReview ? "EDIT REVIEW" : "RATE COURSE"}
          </button>
        )}
        {pct === 100 && (
          <Link to={`/certificate/${id}`} data-testid="get-certificate-btn"
            className="text-xs font-display px-3 py-1.5 rounded border transition-colors hover:bg-[rgba(245,192,78,.12)]"
            style={{ color: "var(--gold)", borderColor: "rgba(245,192,78,.5)", letterSpacing: 1 }}>
            🏆 CERTIFICATE
          </Link>
        )}
        <span className="text-sm text-[var(--forge-green)] font-display" data-testid="player-progress">{pct}% COMPLETE</span>
      </div>
      {showReview && (
        <ReviewDialog courseId={id} initial={myReview} onClose={() => setShowReview(false)}
          onSaved={(r) => setMyReview(r)} />
      )}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {unseen.length > 0 && (
            <div className="mb-3 px-4 py-2.5 rounded-lg flex flex-wrap items-center gap-2 text-sm"
              style={{ background: "rgba(230,181,74,.08)", border: "1px solid rgba(230,181,74,.4)" }}
              data-testid="qa-answer-alert">
              <span style={{ color: "var(--gold)" }} className="font-semibold">📬 Instructor replied to your question{unseen.length > 1 ? "s" : ""} on:</span>
              {unseen.map((u) => (
                <button key={u.id} data-testid={`qa-alert-jump-${u.id}`}
                  onClick={() => { const l = allLects.find((x) => x.id === u.lecture_id); if (l) selectLecture(l); }}
                  className="underline hover:no-underline" style={{ color: "var(--bp)" }}>
                  {u.lecture_title}
                </button>
              ))}
            </div>
          )}
          {current && (
            <>
              {current.video_url ? (
                <>
                  <video key={current.id + (effQ || "src")} ref={videoRef}
                    poster={current.video_url.includes("/api/media/") ? mediaUrl(current.video_url.split("?")[0] + "/thumb") : undefined}
                    src={mediaUrl(current.video_url) + (effQ ? `?q=${effQ}` : "")} controls autoPlay
                    controlsList="nodownload" onContextMenu={(e) => e.preventDefault()}
                    onEnded={onEnded} onLoadedMetadata={onLoaded} onPause={onPause} onWaiting={onWaiting}
                    className="w-full aspect-video bg-black rounded-lg" data-testid="lecture-video" />
                  <div className="relative flex justify-end mt-2">
                    <button data-testid="player-settings-btn"
                      onClick={() => { setSettingsOpen((o) => !o); setSettingsView("root"); }}
                      className="text-xs px-3 py-1.5 rounded border transition-colors flex items-center gap-1.5"
                      style={{ color: "var(--muted)", borderColor: "var(--line)" }}>
                      ⚙ {qualityLabel}{speed !== 1 ? ` · ${speed}x` : ""}
                    </button>
                    {settingsOpen && (
                      <div className="absolute bottom-full right-0 mb-2 rounded-lg overflow-hidden z-20 min-w-[230px]"
                        style={{ background: "#121821", border: "1px solid var(--line)", boxShadow: "0 10px 30px rgba(0,0,0,.5)" }}
                        data-testid="player-settings-menu">
                        {settingsView === "root" && (
                          <>
                            <button className="w-full flex justify-between items-center px-4 py-2.5 text-sm hover:bg-[rgba(56,182,255,.08)]"
                              data-testid="settings-speed" onClick={() => setSettingsView("speed")}>
                              <span>⏩ Playback speed</span><span style={{ color: "var(--muted)" }}>{speed === 1 ? "Normal" : `${speed}x`} ›</span>
                            </button>
                            <button className="w-full flex justify-between items-center px-4 py-2.5 text-sm hover:bg-[rgba(56,182,255,.08)]"
                              data-testid="settings-quality" onClick={() => setSettingsView("quality")}>
                              <span>🎞 Quality</span><span style={{ color: "var(--muted)" }}>{qualityLabel} ›</span>
                            </button>
                          </>
                        )}
                        {settingsView === "speed" && (
                          <>
                            <button className="w-full text-left px-4 py-2 text-xs" style={{ color: "var(--muted)" }} onClick={() => setSettingsView("root")}>‹ Playback speed</button>
                            {[0.5, 0.75, 1, 1.25, 1.5, 1.75, 2].map((s) => (
                              <button key={s} data-testid={`speed-${s}`}
                                onClick={() => { selectSpeed(s); setSettingsOpen(false); }}
                                className="w-full text-left px-4 py-2 text-sm hover:bg-[rgba(56,182,255,.08)]"
                                style={speed === s ? { color: "var(--bp)" } : {}}>
                                {speed === s ? "✓ " : ""}{s === 1 ? "Normal" : `${s}x`}
                              </button>
                            ))}
                          </>
                        )}
                        {settingsView === "quality" && (
                          <>
                            <button className="w-full text-left px-4 py-2 text-xs" style={{ color: "var(--muted)" }} onClick={() => setSettingsView("root")}>‹ Quality</button>
                            {qualities.length > 1 && (
                              <button data-testid="quality-auto" onClick={() => { selectQuality("auto"); setSettingsOpen(false); }}
                                className="w-full text-left px-4 py-2 text-sm hover:bg-[rgba(56,182,255,.08)]"
                                style={selected === "auto" ? { color: "var(--bp)" } : {}}>
                                {selected === "auto" ? "✓ " : ""}Auto ({has480 ? "480p" : srcLabel})
                              </button>
                            )}
                            {qualities.map((opt) => (
                              <button key={opt.label} data-testid={`quality-${opt.label}`}
                                onClick={() => { selectQuality(opt.q); setSettingsOpen(false); }}
                                className="w-full text-left px-4 py-2 text-sm hover:bg-[rgba(56,182,255,.08)]"
                                style={selected === opt.q ? { color: "var(--bp)" } : {}}>
                                {selected === opt.q ? "✓ " : ""}{opt.label}
                              </button>
                            ))}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="w-full aspect-video bg-[var(--forge-surface)] rounded-lg flex items-center justify-center text-[var(--forge-dim)]" data-testid="no-video">
                  Video coming soon for this lecture
                </div>
              )}
              <div className="flex items-center justify-between mt-4">
                <h2 className="font-semibold" data-testid="current-lecture-title">{current.title}</h2>
                <button data-testid="mark-complete-btn"
                  onClick={() => save(current, !progMap[current.id]?.completed, videoRef.current?.currentTime || 0)}
                  className={`text-sm font-display px-3 py-1.5 rounded border transition-colors ${progMap[current.id]?.completed ? "border-[var(--forge-green)] text-[var(--forge-green)]" : "border-[var(--forge-border)] text-[var(--forge-dim)] hover:border-[var(--forge-green)]"}`}>
                  {progMap[current.id]?.completed ? "✓ COMPLETED" : "MARK COMPLETE"}
                </button>
              </div>
              <LectureQA courseId={id} lectureId={current.id}
                onAnswersSeen={(lid) => setUnseen((u) => u.filter((x) => x.lecture_id !== lid))} />
              {anns.length > 0 && (
                <div className="mt-6" data-testid="announcements-section">
                  <h3 className="font-display font-bold text-sm mb-3" style={{ letterSpacing: 1 }}>📢 ANNOUNCEMENTS</h3>
                  <div className="space-y-2">
                    {anns.map((a) => (
                      <div key={a.id} className="forge-card p-4" data-testid={`announcement-${a.id}`}>
                        <div className="flex items-center justify-between">
                          <p className="font-semibold text-sm">{a.title}</p>
                          <span className="text-xs" style={{ color: "var(--muted)" }}>{new Date(a.created_at).toLocaleDateString()}</span>
                        </div>
                        {a.body && <p className="text-sm mt-1 whitespace-pre-line" style={{ color: "var(--muted)" }}>{a.body}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        <div className="forge-card !transform-none p-3 h-fit max-h-[75vh] overflow-y-auto" data-testid="player-curriculum">
          {course.curriculum.map((s) => (
            <div key={s.id} className="mb-3">
              <p className="font-display text-xs text-[var(--forge-dim)] tracking-wider px-2 py-1">{s.title.toUpperCase()}</p>
              {s.lectures.map((l) => (
                <button key={l.id} data-testid={`player-lecture-${l.id}`} onClick={() => selectLecture(l)}
                  className={`w-full flex items-center gap-2 px-2 py-2 rounded text-left text-sm transition-colors ${current?.id === l.id ? "bg-[rgba(56,189,248,0.1)] text-[var(--forge-blue)]" : "hover:bg-[var(--forge-surface)]"}`}>
                  {progMap[l.id]?.completed ? <CheckCircle2 size={15} className="text-[var(--forge-green)] shrink-0" /> : <Circle size={15} className="text-[var(--forge-dim)] shrink-0" />}
                  <span className="flex-1 line-clamp-1">{l.title}</span>
                  <span className="text-xs text-[var(--forge-dim)]">{l.duration_sec ? `${Math.floor(l.duration_sec / 60)}:${String(l.duration_sec % 60).padStart(2, "0")}` : `${l.duration_min}m`}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
